import React, { useEffect, useState, useRef } from 'react';
import { Box, Button, Divider, Grid, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ContactPageOutlined from '@mui/icons-material/ContactPageOutlined';
import WorkOutlineOutlined from '@mui/icons-material/WorkOutlineOutlined';
import FilterAltOutlined from '@mui/icons-material/FilterAltOutlined';
import PersonOutlineOutlined from '@mui/icons-material/PersonOutlineOutlined';
import TaskAltOutlined from '@mui/icons-material/TaskAltOutlined';
import AudioFileOutlined from '@mui/icons-material/AudioFileOutlined';
import CameraEnhanceIcon from '@mui/icons-material/CameraEnhance';
import Settings from '@mui/icons-material/Settings';
import DocumentScannerOutlined from '@mui/icons-material/DocumentScannerOutlined';
import KeyboardArrowUpRounded from '@mui/icons-material/KeyboardArrowUpRounded';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { BottomSheet } from 'react-spring-bottom-sheet';
import 'react-spring-bottom-sheet/dist/style.css';
import '../index.css';
import Api from '../Services/Api';
import { useTranslation } from 'react-i18next';

export const COLLAPSED_HEIGHT = 104;
const TAB_ICON = 26;
const CARD_ICON = 40;

const Layout = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [size, setSize] = useState(COLLAPSED_HEIGHT + 8);
  const [hide, setHide] = useState(false);
  const { t } = useTranslation();
  const [logged, setLogged] = useState(false);
  const sheetRef = useRef(null);
  const [user, setUser] = useState(null);

  const accent = theme.palette.secondary.main;
  const darkBg = theme.palette.info.main;
  const headerBg = 'rgba(16, 26, 34, 0.94)';

  const isActive = (path) => {
    if (path === '/app') return pathname === '/app';
    return pathname.startsWith(path);
  };

  const isItemVisible = (item) => {
    if (!item.role) return true;
    return user?.role?.includes(item.role) ?? false;
  };

  const handleNavigate = (path) => {
    navigate(path);
    sheetRef.current?.snapTo(({ snapPoints }) => Math.min(...snapPoints));
  };

  const handleExpand = () => {
    sheetRef.current?.snapTo(({ snapPoints }) => Math.max(...snapPoints));
  };

  const primaryItems = [
    { label: t('contacts'), icon: <ContactPageOutlined sx={{ fontSize: TAB_ICON }} />, path: '/app/contacts', testId: 'nav-contacts' },
    { label: t('companies'), icon: <WorkOutlineOutlined sx={{ fontSize: TAB_ICON }} />, path: '/app/companies', testId: 'nav-companies' },
    { label: t('deals'), icon: <FilterAltOutlined sx={{ fontSize: TAB_ICON }} />, path: '/app/deals', testId: 'nav-deals' },
    { label: t('tasks'), icon: <TaskAltOutlined sx={{ fontSize: TAB_ICON }} />, path: '/app/tasks', testId: 'nav-tasks' },
  ];

  const secondaryItems = [
    { label: t('account'), icon: <PersonOutlineOutlined sx={{ fontSize: CARD_ICON }} />, path: '/account', testId: 'nav-account' },
    { label: t('audioHistory'), icon: <AudioFileOutlined sx={{ fontSize: CARD_ICON }} />, path: '/app/audio', testId: 'nav-audio', role: 'history' },
    { label: t('businessCard'), icon: <CameraEnhanceIcon sx={{ fontSize: CARD_ICON }} />, path: '/app/business-card', testId: 'nav-business-card' },
  ];

  const adminItems = [
    { label: t('properties'), icon: <Settings sx={{ fontSize: CARD_ICON }} />, path: '/app/property-chat', testId: 'nav-properties', role: 'admin' },
    { label: t('ocr'), icon: <DocumentScannerOutlined sx={{ fontSize: CARD_ICON }} />, path: '/app/ocr', testId: 'nav-ocr', role: 'ocr' },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
    Api.getUserData().then((response) => {
      setLogged(true);
      setUser(response);
    }).catch(() => {
      setLogged(false);
      navigate("/");
    });
  }, [pathname]);

  const transition = 'color 200ms ease-out, background-color 200ms ease-out, transform 150ms ease-out, box-shadow 200ms ease-out';
  const noMotion = { '@media (prefers-reduced-motion: reduce)': { transition: 'none' } };

  const primaryTabSx = (active) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 0,
    flex: 1,
    py: 0.5,
    px: 0.25,
    mx: 0.25,
    color: active ? '#fff' : 'rgba(255, 255, 255, 0.72)',
    backgroundColor: active ? 'rgba(118, 169, 172, 0.22)' : 'transparent',
    borderRadius: '10px',
    transition,
    ...noMotion,
    '&:active': { transform: 'scale(0.93)', opacity: 0.85 },
    '&:hover': { backgroundColor: active ? 'rgba(118, 169, 172, 0.30)' : 'rgba(255, 255, 255, 0.08)' },
    '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: 2 },
  });

  const sheetCardSx = (active) => ({
    backgroundColor: active ? accent : 'rgba(22, 34, 44, 0.95)',
    color: active ? darkBg : '#fff',
    width: '100%',
    height: '100%',
    borderRadius: '16px',
    border: `1px solid ${active ? accent : 'rgba(255, 255, 255, 0.12)'}`,
    textTransform: 'none',
    transition,
    ...noMotion,
    '&:active': { transform: 'scale(0.96)', opacity: 0.85 },
    '&:hover': {
      backgroundColor: active ? '#578386' : 'rgba(30, 46, 58, 0.98)',
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: 2 },
  });

  const visibleSecondary = secondaryItems.filter(isItemVisible);
  const visibleAdmin = adminItems.filter(isItemVisible);

  const renderPrimaryNav = () => (
    <Box
      sx={{
        backgroundColor: headerBg,
        borderRadius: '14px 14px 0 0',
        mx: '-1px',
        pt: 0.5,
        pb: 0.25,
      }}
    >
      <Box
        role="navigation"
        aria-label={t('primaryNavigation', 'Primary navigation')}
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-around',
          width: '100%',
          pt: 0.75,
          px: 0.5,
        }}
      >
        {primaryItems.slice(0, 2).map((item) => (
          <Button
            key={item.path}
            data-testid={item.testId}
            aria-label={item.label}
            aria-current={isActive(item.path) ? 'page' : undefined}
            onClick={() => handleNavigate(item.path)}
            sx={primaryTabSx(isActive(item.path))}
          >
            {item.icon}
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: isActive(item.path) ? 600 : 400,
                textTransform: 'capitalize',
                lineHeight: 1.2,
                mt: 0.5,
              }}
            >
              {item.label}
            </Typography>
          </Button>
        ))}

        <Button
          data-testid="nav-record"
          aria-label={t('record')}
          aria-current={isActive('/app') ? 'page' : undefined}
          onClick={() => handleNavigate('/app')}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 0,
            flex: 1,
            py: 0.5,
            px: 0.25,
            mx: 0.25,
            borderRadius: '10px',
            color: isActive('/app') ? '#fff' : 'rgba(255, 255, 255, 0.72)',
            backgroundColor: isActive('/app') ? 'rgba(118, 169, 172, 0.22)' : 'transparent',
            transition,
            ...noMotion,
            '&:active': { transform: 'scale(0.93)', opacity: 0.85 },
            '&:hover': { backgroundColor: isActive('/app') ? 'rgba(118, 169, 172, 0.30)' : 'rgba(255, 255, 255, 0.08)' },
            '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: 2 },
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img src="/labia_logo.png" alt="" style={{ width: 26, height: 26 }} />
          </Box>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: isActive('/app') ? 600 : 400,
              textTransform: 'capitalize',
              lineHeight: 1.2,
              mt: 0.5,
            }}
          >
            {t('record')}
          </Typography>
        </Button>

        {primaryItems.slice(2).map((item) => (
          <Button
            key={item.path}
            data-testid={item.testId}
            aria-label={item.label}
            aria-current={isActive(item.path) ? 'page' : undefined}
            onClick={() => handleNavigate(item.path)}
            sx={primaryTabSx(isActive(item.path))}
          >
            {item.icon}
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: isActive(item.path) ? 600 : 400,
                textTransform: 'capitalize',
                lineHeight: 1.2,
                mt: 0.5,
              }}
            >
              {item.label}
            </Typography>
          </Button>
        ))}
      </Box>

      <Box
        data-testid="nav-menu-button"
        role="button"
        tabIndex={0}
        aria-label={t('expandMenu', 'Expand menu')}
        onClick={handleExpand}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleExpand(); }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 0,
          pb: 0.25,
          cursor: 'pointer',
          '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: 2, borderRadius: '4px' },
        }}
      >
        <KeyboardArrowUpRounded sx={{ fontSize: 18, color: 'rgba(255, 255, 255, 0.35)', transition: 'color 200ms ease-out', ...noMotion }} />
        <Box sx={{ width: 32, height: 4, borderRadius: 2, backgroundColor: 'rgba(255, 255, 255, 0.25)', mt: -0.25 }} />
      </Box>
    </Box>
  );

  return (
    <div data-body-scroll-lock-ignore="true">
      <div style={{ padding: `0 0 ${size}px 0` }}>
        <Outlet context={{ setHide, user }} />
      </div>
      {logged && !hide && (
        <BottomSheet
          open
          scrollLocking
          ref={sheetRef}
          blocking={false}
          expandOnContentDrag={true}
          snapPoints={({ maxHeight }) => {
            setSize(COLLAPSED_HEIGHT + 8);
            return [COLLAPSED_HEIGHT, maxHeight * 0.9];
          }}
          header={renderPrimaryNav()}
        >
          {visibleSecondary.length > 0 && (
            <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
              <Typography
                variant="overline"
                sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 11, letterSpacing: '0.08em', fontWeight: 600, pl: 0.5 }}
              >
                {t('more', 'More')}
              </Typography>
            </Box>
          )}
          <Grid container role="navigation" aria-label={t('secondaryNavigation', 'Secondary navigation')} sx={{ px: 1 }}>
            {visibleSecondary.map((item) => (
              <Grid item xs={6} key={item.path} sx={{ p: 1 }}>
                <Button
                  data-testid={item.testId}
                  aria-label={item.label}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                  onClick={() => handleNavigate(item.path)}
                  sx={sheetCardSx(isActive(item.path))}
                >
                  <Stack spacing={1} sx={{ py: 2, alignItems: 'center' }}>
                    {item.icon}
                    <Typography sx={{ fontSize: 14, fontWeight: 500, textTransform: 'capitalize' }}>
                      {item.label}
                    </Typography>
                  </Stack>
                </Button>
              </Grid>
            ))}
          </Grid>

          {visibleAdmin.length > 0 && (
            <>
              <Divider sx={{ mx: 2, my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
              <Box sx={{ px: 2, pt: 0.5, pb: 0.5 }}>
                <Typography
                  variant="overline"
                  sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 11, letterSpacing: '0.08em', fontWeight: 600, pl: 0.5 }}
                >
                  {t('admin', 'Admin')}
                </Typography>
              </Box>
              <Grid container role="navigation" aria-label={t('adminNavigation', 'Admin navigation')} sx={{ px: 1, pb: 2 }}>
                {visibleAdmin.map((item) => (
                  <Grid item xs={6} key={item.path} sx={{ p: 1 }}>
                    <Button
                      data-testid={item.testId}
                      aria-label={item.label}
                      aria-current={isActive(item.path) ? 'page' : undefined}
                      onClick={() => handleNavigate(item.path)}
                      sx={sheetCardSx(isActive(item.path))}
                    >
                      <Stack spacing={1} sx={{ py: 2, alignItems: 'center' }}>
                        {item.icon}
                        <Typography sx={{ fontSize: 14, fontWeight: 500, textTransform: 'capitalize' }}>
                          {item.label}
                        </Typography>
                      </Stack>
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </BottomSheet>
      )}
    </div>
  );
};

export default Layout;
